/**
 * Timed effects of Essence abilities on a character (`essenceStatuses`):
 * finding the active ones, adding and pruning them, and summing the combat
 * modifiers they add on top of a character's crystal effects. The host owns
 * the statuses; see crucible-host `essence-ability-effects`.
 */

import type { BehaviorStackDefinition } from './types/behaviors';
import type { BehaviorStack, Character, CombatModifierState, EssenceStatus, EssenceStatusKind } from './types/game';
import { assertNever } from './utils';

/**
 * How several of one combat modifier fold together: most add; one that
 * stands in place of a base number takes the highest or lowest; a factor
 * multiplies.
 */
export type CombatModifierFold = 'sum' | 'max' | 'min' | 'product';

/** Every combat modifier and how it folds, in a fixed order. */
export const COMBAT_MODIFIER_FOLDS: Readonly<Record<keyof CombatModifierState, CombatModifierFold>> = {
    hitBonus: 'sum',
    avoidanceBonus: 'sum',
    flatDamage: 'sum',
    damageMultiplier: 'sum',
    mitigationBonus: 'sum',
    avoidanceMultiplier: 'sum',
    damageTakenMultiplier: 'sum',
    baseDamageMultiplier: 'sum',
    landedDamageFactor: 'product',
    impactFloor: 'max',
    weakPointChance: 'max',
    weakPointMultiplier: 'max',
    partialBlockBypass: 'max',
    extraTargets: 'sum',
    partialBlockPassFraction: 'min',
    blockChanceBonus: 'sum',
    blockChanceCap: 'max',
    rangedParryShare: 'max',
    parryRecoveryFactor: 'min',
    weaponAvoidanceBonus: 'sum',
    mitigationShred: 'sum',
    attackCooldownReduction: 'sum',
};

/** Every combat modifier, in a fixed order. */
export const COMBAT_MODIFIER_KEYS = Object.keys(COMBAT_MODIFIER_FOLDS) as ReadonlyArray<keyof CombatModifierState>;

function foldCombatModifier(fold: CombatModifierFold, total: number | undefined, value: number): number {
    if (total === undefined) {
        return value;
    }
    switch (fold) {
        case 'sum':
            return total + value;
        case 'max':
            return Math.max(total, value);
        case 'min':
            return Math.min(total, value);
        case 'product':
            return total * value;
        default:
            return assertNever(fold);
    }
}

/** Several sets of combat modifiers folded together (`COMBAT_MODIFIER_FOLDS`); a modifier none of them set is left out. */
export function resolveCombinedCombatModifiers(modifiers: ReadonlyArray<CombatModifierState | undefined>): CombatModifierState {
    const total: CombatModifierState = {};
    for (const entry of modifiers) {
        if (!entry) {
            continue;
        }
        for (const key of COMBAT_MODIFIER_KEYS) {
            const value = entry[key];
            if (value !== undefined) {
                total[key] = foldCombatModifier(COMBAT_MODIFIER_FOLDS[key], total[key], value);
            }
        }
    }
    return total;
}

/** A behavior stack still in effect at `nowMs`. */
export function findBehaviorStack(character: Pick<Character, 'behaviorStacks'>, stackId: string, nowMs: number): BehaviorStack | undefined {
    return character.behaviorStacks?.find((stack) => stack.stackId === stackId && stack.expiresAtMs > nowMs);
}

/** Adds a stack (`BehaviorStackDefinition`), up to its cap, from `sourceId`, and restarts its timer. */
export function addBehaviorStack(character: Pick<Character, 'behaviorStacks'>, definition: BehaviorStackDefinition, sourceId: string, nowMs: number): void {
    const stacks = Math.min(definition.maxStacks, (findBehaviorStack(character, definition.stackId, nowMs)?.stacks ?? 0) + 1);
    const others = (character.behaviorStacks ?? []).filter((stack) => stack.stackId !== definition.stackId && stack.expiresAtMs > nowMs);
    character.behaviorStacks = [...others, {
        stackId: definition.stackId,
        sourceId,
        stacks,
        expiresAtMs: nowMs + definition.durationMs,
        ...(definition.perStack ? { perStack: definition.perStack } : {}),
    }];
}

/** Clears a stack from `character`. */
export function clearBehaviorStack(character: Pick<Character, 'behaviorStacks'>, stackId: string): void {
    const remaining = (character.behaviorStacks ?? []).filter((stack) => stack.stackId !== stackId);
    if (remaining.length === 0) {
        delete character.behaviorStacks;
    } else {
        character.behaviorStacks = remaining;
    }
}

/** What a character's behavior stacks add at `nowMs`: each stack's per-stack modifiers, once per stack. */
export function resolveBehaviorStackModifiers(character: Pick<Character, 'behaviorStacks'>, nowMs: number): CombatModifierState {
    const scaled = (character.behaviorStacks ?? [])
        .filter((stack) => stack.expiresAtMs > nowMs && stack.perStack)
        .map((stack) => Object.fromEntries(COMBAT_MODIFIER_KEYS.flatMap((key) => {
            const value = stack.perStack?.[key];
            if (value === undefined) {
                return [];
            }
            // Only what adds up grows with the stacks; anything else applies once.
            return [[key, COMBAT_MODIFIER_FOLDS[key] === 'sum' ? value * stack.stacks : value]];
        })) as CombatModifierState);
    return resolveCombinedCombatModifiers(scaled);
}

/** A character's statuses still in effect at `nowMs`. */
export function listActiveEssenceStatuses(character: Pick<Character, 'essenceStatuses'>, nowMs: number): EssenceStatus[] {
    return (character.essenceStatuses ?? []).filter((status) => status.expiresAtMs > nowMs);
}

/** A status of `kind` in effect at `nowMs`, optionally only one set by `sourceId`. */
export function findEssenceStatus(
    character: Pick<Character, 'essenceStatuses'>,
    kind: EssenceStatusKind,
    nowMs: number,
    sourceId?: string,
): EssenceStatus | undefined {
    return character.essenceStatuses?.find((status) => status.kind === kind
        && status.expiresAtMs > nowMs
        && (sourceId === undefined || status.sourceId === sourceId));
}

/**
 * Starts a status, replacing one of the same kind from the same source:
 * casting again refreshes rather than stacks. Different casters' statuses of
 * one kind stack.
 */
export function addEssenceStatus(character: Pick<Character, 'essenceStatuses'>, status: EssenceStatus): void {
    const others = (character.essenceStatuses ?? [])
        .filter((existing) => existing.kind !== status.kind || existing.sourceId !== status.sourceId);
    character.essenceStatuses = [...others, status];
}

/** Ends statuses of `kind` (from `sourceId`, if given). True when any ended. */
export function removeEssenceStatus(
    character: Pick<Character, 'essenceStatuses'>,
    kind: EssenceStatusKind,
    sourceId?: string,
): boolean {
    const before = character.essenceStatuses ?? [];
    const remaining = before.filter((status) => status.kind !== kind || (sourceId !== undefined && status.sourceId !== sourceId));
    if (remaining.length === before.length) {
        return false;
    }
    if (remaining.length === 0) {
        delete character.essenceStatuses;
    } else {
        character.essenceStatuses = remaining;
    }
    return true;
}

/** Drops expired statuses and those whose charges ran out. True when any were dropped. */
export function pruneEssenceStatuses(character: Pick<Character, 'essenceStatuses'>, nowMs: number): boolean {
    const before = character.essenceStatuses;
    if (!before) {
        return false;
    }
    const remaining = before.filter((status) => status.expiresAtMs > nowMs && (status.charges === undefined || status.charges > 0));
    if (remaining.length === before.length) {
        return false;
    }
    if (remaining.length === 0) {
        delete character.essenceStatuses;
    } else {
        character.essenceStatuses = remaining;
    }
    return true;
}

/**
 * A character's combat modifiers at `nowMs`: their crystal effects'
 * (`combatModifiers`) plus those of their active Essence statuses and
 * behavior stacks.
 */
export function resolveEffectiveCombatModifiers(
    character: Pick<Character, 'combatModifiers' | 'essenceStatuses' | 'behaviorStacks'>,
    nowMs: number,
): CombatModifierState {
    const statuses = listActiveEssenceStatuses(character, nowMs);
    if (statuses.length === 0 && !character.behaviorStacks?.length) {
        return character.combatModifiers ?? {};
    }
    return resolveCombinedCombatModifiers([
        character.combatModifiers,
        ...statuses.map((status) => status.modifiers),
        resolveBehaviorStackModifiers(character, nowMs),
    ]);
}

/** The share of normal attack cooldown a character's statuses leave (Frenzy); 1 when none speed it up. */
export function resolveStatusAttackCooldownFactor(character: Pick<Character, 'essenceStatuses'>, nowMs: number): number {
    return listActiveEssenceStatuses(character, nowMs)
        .reduce((factor, status) => factor * (status.attackCooldownFactor ?? 1), 1);
}

/** The share of normal speed a character's statuses leave it (Heavy Door); 1 when none slow it. */
export function resolveStatusMoveSpeedFactor(character: Pick<Character, 'essenceStatuses'>, nowMs: number): number {
    return listActiveEssenceStatuses(character, nowMs)
        .reduce((factor, status) => factor * (status.moveSpeedFactor ?? 1), 1);
}
